import { ClinicalMeasurement } from "../types";
import { PatientAgeInfo } from "../types";

export const VANDERBILT_MIN_AGE = 4;
export const VANDERBILT_MAX_AGE = 18;
export const REASSESSMENT_RATE_DAYS = 90;

// Vanderbilt applicability is AGE-ONLY (captain decision 2026-07-06, F15): any patient
// aged 4–18 is eligible for the ADHD Vanderbilt scales. We deliberately do NOT gate on
// an ADHD (F90.x) diagnosis code — no diagnosis source exists, and clinically the
// Vanderbilt is an age-appropriate screening instrument. Diagnosis codes still gate the
// PHQ-9/GAD-7 screeners (see screenerUtils.isScreenerApplicable), which are unchanged.
export const isVanderbiltApplicable = (
  patientAge: PatientAgeInfo | null | undefined,
): boolean => {
  if (!patientAge) {
    return false;
  }

  return (
    patientAge.years >= VANDERBILT_MIN_AGE &&
    patientAge.years <= VANDERBILT_MAX_AGE
  );
};

export const computeNextDueDate = (
  lastCompletedStr: string | null | undefined,
): string => {
  if (!lastCompletedStr || lastCompletedStr === "N/A") {
    return "N/A";
  }

  const lastCompleted = new Date(lastCompletedStr);
  if (isNaN(lastCompleted.getTime())) {
    return "N/A";
  }

  const nextDue = new Date(lastCompleted.getTime());
  nextDue.setDate(nextDue.getDate() + REASSESSMENT_RATE_DAYS);

  return nextDue.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
};

export const computeIsOverdue = (
  nextDueStr: string | null | undefined,
): boolean => {
  if (!nextDueStr || nextDueStr === "N/A") {
    return false;
  }

  const nextDue = new Date(nextDueStr);
  if (isNaN(nextDue.getTime())) {
    return false;
  }

  return new Date() > nextDue;
};

// Main function to process the fetched measurements and determine their correct status
export const computeVanderbiltStatus = (
  fetchedMeasures: (ClinicalMeasurement | null)[],
  patientAge: PatientAgeInfo | null | undefined,
): ClinicalMeasurement[] => {
  const isApplicable = isVanderbiltApplicable(patientAge);

  // Group fetched measures by name for easy access
  const measureMap: Record<string, ClinicalMeasurement> = {};
  fetchedMeasures.forEach((measure) => {
    if (measure && measure.measureName) {
      measureMap[measure.measureName] = measure;
    }
  });

  const processedMeasures: ClinicalMeasurement[] = [];

  const parentInitialName = "Vanderbilt: Parent Initial Assessment";
  const teacherInitialName = "Vanderbilt: Teacher Initial Assessment";
  const parentReassessmentName = "Vanderbilt: Parent Reassessment";
  const teacherReassessmentName = "Vanderbilt: Teacher Reassessment";

  // If not applicable, we just pass through existing data but set status to 'not-applicable'
  if (!isApplicable) {
    fetchedMeasures.forEach((measure) => {
      if (measure) {
        processedMeasures.push({
          ...measure,
          status: "not-applicable",
          statusExplanation:
            "Patient's age does not meet the criteria for this assessment (ages 4–18).",
          nextDue: "N/A",
          isOverdue: false,
        });
      }
    });
    return processedMeasures;
  }

  // --- APPLICABLE PATIENT LOGIC ---

  const parentInitial = measureMap[parentInitialName];
  const teacherInitial = measureMap[teacherInitialName];
  const parentReassessment = measureMap[parentReassessmentName];
  const teacherReassessment = measureMap[teacherReassessmentName];

  // A measure is only truly "present" if it has data (not an empty shell)
  const hasParentInitial =
    parentInitial &&
    parentInitial.longitudinalData &&
    parentInitial.longitudinalData.length > 0;
  const hasTeacherInitial =
    teacherInitial &&
    teacherInitial.longitudinalData &&
    teacherInitial.longitudinalData.length > 0;
  const hasParentReassessment =
    parentReassessment &&
    parentReassessment.longitudinalData &&
    parentReassessment.longitudinalData.length > 0;
  const hasTeacherReassessment =
    teacherReassessment &&
    teacherReassessment.longitudinalData &&
    teacherReassessment.longitudinalData.length > 0;

  // Helper to create an empty/dummy measure if missing
  const createEmptyMeasure = (
    name: string,
    status: ClinicalMeasurement["status"],
    explanation?: string,
  ): ClinicalMeasurement => ({
    measureName: name,
    status: status,
    statusExplanation: explanation,
    latestScore: 0,
    trendData: [],
    lastCompleted: "N/A",
    nextDue: "N/A",
    isOverdue: status === "off-track", // If it's off-track and empty, it's essentially overdue
    subscales: [],
    subscaleTrendData: {},
    longitudinalData: [],
    rawData: [],
  });

  // 1. Process Parent Initial
  if (hasParentInitial) {
    // If they have it, it's on-track
    processedMeasures.push({
      ...parentInitial,
      status: "on-track",
      statusExplanation: "Initial assessment has been completed.",
      nextDue: "N/A", // Initial assessments don't have a next due date once completed
      isOverdue: false,
    });
  } else if (hasParentReassessment) {
    // Missing Parent Initial but has Reassessment -> on-track (assume completed in old system)
    processedMeasures.push(
      createEmptyMeasure(
        parentInitialName,
        "on-track",
        "Initial assessment was completed in a previous system.",
      ),
    );
  } else {
    // Missing Parent Initial -> off-track
    processedMeasures.push(
      createEmptyMeasure(
        parentInitialName,
        "off-track",
        "Initial assessment is missing.",
      ),
    );
  }

  // 2. Process Teacher Initial
  if (hasTeacherInitial) {
    // If they have it, it's on-track
    processedMeasures.push({
      ...teacherInitial,
      status: "on-track",
      statusExplanation: "Initial assessment has been completed.",
      nextDue: "N/A",
      isOverdue: false,
    });
  } else if (hasTeacherReassessment) {
    // Missing Teacher Initial but has Reassessment -> on-track (assume completed in old system)
    processedMeasures.push(
      createEmptyMeasure(
        teacherInitialName,
        "on-track",
        "Initial assessment was completed in a previous system.",
      ),
    );
  } else {
    // Missing Teacher Initial -> off-track
    processedMeasures.push(
      createEmptyMeasure(
        teacherInitialName,
        "off-track",
        "Initial assessment is missing.",
      ),
    );
  }

  // 3. Process Parent Reassessment
  // Reassessments are based on the latest date of either the initial OR the previous reassessment
  if (hasParentInitial || hasParentReassessment) {
    let lastParentDateStr = "N/A";

    if (hasParentInitial && parentInitial.lastCompleted !== "N/A") {
      lastParentDateStr = parentInitial.lastCompleted;
    }

    if (hasParentReassessment && parentReassessment.lastCompleted !== "N/A") {
      // If there are reassessments, find the absolute latest date between initial and reassessments
      const reassessDate = new Date(parentReassessment.lastCompleted);

      if (lastParentDateStr !== "N/A") {
        const initialDate = new Date(lastParentDateStr);
        if (!isNaN(initialDate.getTime()) && !isNaN(reassessDate.getTime())) {
          lastParentDateStr =
            initialDate > reassessDate
              ? lastParentDateStr
              : parentReassessment.lastCompleted;
        } else if (!isNaN(reassessDate.getTime())) {
          lastParentDateStr = parentReassessment.lastCompleted;
        }
      } else if (!isNaN(reassessDate.getTime())) {
        lastParentDateStr = parentReassessment.lastCompleted;
      }
    }

    const nextDue = computeNextDueDate(lastParentDateStr);
    const isOverdue = computeIsOverdue(nextDue);

    if (hasParentReassessment) {
      processedMeasures.push({
        ...parentReassessment,
        status: isOverdue ? "off-track" : "on-track",
        statusExplanation: isOverdue
          ? `Reassessment is overdue. It was due on ${nextDue}.`
          : `Reassessment is up to date. Next due on ${nextDue}.`,
        nextDue: nextDue,
        isOverdue: isOverdue,
      });
    } else {
      // Missing Reassessment but they have an Initial
      const emptyMeasure = createEmptyMeasure(
        parentReassessmentName,
        isOverdue ? "off-track" : "on-track",
        isOverdue
          ? `Reassessment is missing and overdue. It was due on ${nextDue}.`
          : `Reassessment is on track. Next due on ${nextDue}.`,
      );
      emptyMeasure.nextDue = nextDue;
      processedMeasures.push(emptyMeasure);
    }
  }

  // 4. Process Teacher Reassessment
  if (hasTeacherInitial || hasTeacherReassessment) {
    let lastTeacherDateStr = "N/A";

    if (hasTeacherInitial && teacherInitial.lastCompleted !== "N/A") {
      lastTeacherDateStr = teacherInitial.lastCompleted;
    }

    if (hasTeacherReassessment && teacherReassessment.lastCompleted !== "N/A") {
      const reassessDate = new Date(teacherReassessment.lastCompleted);

      if (lastTeacherDateStr !== "N/A") {
        const initialDate = new Date(lastTeacherDateStr);
        if (!isNaN(initialDate.getTime()) && !isNaN(reassessDate.getTime())) {
          lastTeacherDateStr =
            initialDate > reassessDate
              ? lastTeacherDateStr
              : teacherReassessment.lastCompleted;
        } else if (!isNaN(reassessDate.getTime())) {
          lastTeacherDateStr = teacherReassessment.lastCompleted;
        }
      } else if (!isNaN(reassessDate.getTime())) {
        lastTeacherDateStr = teacherReassessment.lastCompleted;
      }
    }

    const nextDue = computeNextDueDate(lastTeacherDateStr);
    const isOverdue = computeIsOverdue(nextDue);

    if (hasTeacherReassessment) {
      processedMeasures.push({
        ...teacherReassessment,
        status: isOverdue ? "off-track" : "on-track",
        statusExplanation: isOverdue
          ? `Reassessment is overdue. It was due on ${nextDue}.`
          : `Reassessment is up to date. Next due on ${nextDue}.`,
        nextDue: nextDue,
        isOverdue: isOverdue,
      });
    } else {
      // Missing Reassessment but they have an Initial
      const emptyMeasure = createEmptyMeasure(
        teacherReassessmentName,
        isOverdue ? "off-track" : "on-track",
        isOverdue
          ? `Reassessment is missing and overdue. It was due on ${nextDue}.`
          : `Reassessment is on track. Next due on ${nextDue}.`,
      );
      emptyMeasure.nextDue = nextDue;
      processedMeasures.push(emptyMeasure);
    }
  }

  return processedMeasures;
};
